import type { Candidate } from '@/models/job';
import type { ParseKeys } from 'i18next';
import { CandidateStatus } from '@/models/types';
type JobKey = ParseKeys<'job'>;

export const EMPTY_MESSAGE_BY_STATUS = {
  [CandidateStatus.PENDING]: 'table.empty_no_pending',
  [CandidateStatus.SHORTLISTED]: 'table.empty_no_shortlisted',
  [CandidateStatus.INTERVIEWING]: 'table.empty_no_interviewing',
  [CandidateStatus.INTERVIEWED]: 'table.empty_no_interviewed',
  [CandidateStatus.ACCEPTED]: 'table.empty_no_accepted',
  [CandidateStatus.REJECTED]: 'table.empty_no_rejected',
  [CandidateStatus.HIRED]: 'table.empty_no_hired',
} as const satisfies Record<CandidateStatus, JobKey>;

export const EMPTY_HINT_BY_STATUS = {
  [CandidateStatus.PENDING]: 'table.empty_add_cvs_hint',
  [CandidateStatus.SHORTLISTED]: 'table.empty_shortlisted_hint',
  [CandidateStatus.INTERVIEWING]: 'table.empty_interviewing_hint',
  [CandidateStatus.INTERVIEWED]: 'table.empty_interviewed_hint',
  [CandidateStatus.ACCEPTED]: 'table.empty_accepted_hint',
  [CandidateStatus.REJECTED]: 'table.empty_rejected_hint',
  [CandidateStatus.HIRED]: 'table.empty_hired_hint',
} as const satisfies Record<CandidateStatus, JobKey>;

export function emptyStateKeys(
  stageType: string | undefined,
  isQueryEmpty: boolean
): { messageKey: JobKey; hintKey: JobKey } {
  // `stageType ? ... : undefined`, not `stageType && ...`: an empty stageType
  // makes `&&` yield `''`, which `??` keeps, widening the return type past JobKey.
  const hintKey =
    (stageType
      ? EMPTY_HINT_BY_STATUS[stageType as CandidateStatus]
      : undefined) ?? 'table.empty_switch_status';
  if (isQueryEmpty) {
    return { messageKey: 'table.empty_no_match', hintKey };
  }
  return {
    messageKey:
      (stageType
        ? EMPTY_MESSAGE_BY_STATUS[stageType as CandidateStatus]
        : undefined) ?? 'table.empty_no_candidates',
    hintKey,
  };
}

export function isInterviewExpired(expirationDate: string | null | undefined): boolean {
  if (!expirationDate) return false;
  return new Date(expirationDate) < new Date();
}

export function formatPercentScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '-';
  return `${Math.round(score)}%`;
}

/** CV match scores are stored as 0–10000; display as 0–100%. */
export function formatCvMatchScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '-';
  return `${Math.round(score / 100)}%`;
}

export function candidateMatchesSearch(
  candidate: Candidate,
  searchLower: string,
  scores: {
    overall: Record<string, number | null>;
    tech: Record<string, number | null>;
    soft: Record<string, number | null>;
  },
  sourceLabel: string,
  /** Stage name as displayed (localized for untouched default stages). */
  stageLabel: string
): boolean {
  const name = candidate.talentName?.toLowerCase() ?? '';
  if (name.includes(searchLower)) return true;

  const email = candidate.talentEmail?.toLowerCase() ?? '';
  if (email.includes(searchLower)) return true;

  if (sourceLabel.toLowerCase().includes(searchLower)) return true;

  const semantic = candidate.pipelineStageSemantic?.toLowerCase() ?? '';
  if (semantic && semantic.includes(searchLower)) return true;
  const label = candidate.pipelineStageLabel?.toLowerCase() ?? '';
  if (label && label.includes(searchLower)) return true;
  if (stageLabel && stageLabel.toLowerCase().includes(searchLower)) return true;

  const cvScore = candidate.score != null ? Math.round(candidate.score / 100) : null;
  if (cvScore != null && cvScore.toString().includes(searchLower)) return true;

  for (const map of [scores.overall, scores.tech, scores.soft]) {
    const value = map[candidate.id];
    if (value != null && value.toString().includes(searchLower)) return true;
  }

  return false;
}

export function filterCandidates(args: {
  candidates: Candidate[];
  activeStageId: string;
  /** Stage resolution shared with the Kanban (see utils/pipeline-stages). */
  resolveStageId: (candidate: Candidate) => string;
  debouncedSearchTerm: string;
  scores: {
    overall: Record<string, number | null>;
    tech: Record<string, number | null>;
    soft: Record<string, number | null>;
  };
  getSourceLabel: (source: string) => string;
  getStageLabel: (candidate: Candidate) => string;
}): Candidate[] {
  const {
    candidates,
    activeStageId,
    resolveStageId,
    debouncedSearchTerm,
    scores,
    getSourceLabel,
    getStageLabel,
  } = args;

  if (!candidates.length) return [];

  if (debouncedSearchTerm.trim()) {
    const searchLower = debouncedSearchTerm.toLowerCase();
    return candidates.filter((candidate) =>
      candidateMatchesSearch(
        candidate,
        searchLower,
        scores,
        getSourceLabel(candidate.source || 'cv_upload'),
        getStageLabel(candidate)
      )
    );
  }

  return candidates.filter(
    (candidate) => resolveStageId(candidate) === activeStageId
  );
}

export function sortCandidates(args: {
  candidates: Candidate[];
  activeStageType: string | undefined;
  isSearchMode: boolean;
  overallScores: Record<string, number | null>;
}): Candidate[] {
  const { candidates, activeStageType, isSearchMode, overallScores } = args;

  return [...candidates].sort((a, b) => {
    if (activeStageType === 'pending' && !isSearchMode) {
      return (b.score || 0) - (a.score || 0);
    }

    const aScore = overallScores[a.id] ?? a.score ?? 0;
    const bScore = overallScores[b.id] ?? b.score ?? 0;
    return bScore - aScore;
  });
}
